import cn from "classnames";
import { hiddenUserData } from "helpers/hiddenData";
import { paths } from "helpers/paths";
import { columnName, formatValue, typeSpecificProps } from "helpers/reports";
import upperFirst from "lodash/upperFirst";
import Tooltip from "rc-tooltip";
import React from "react";
import CommonReport from "./commonReport";

export default class ResponsibleLogistsReport extends CommonReport {
    cellProps = (column) => {
        return {
            Header: () => column.label,
            accessor: columnName(column),
            Cell: (props) => {
                switch (props.column.id) {
                    case "overallBidsPrice":
                    case "overallBidsPriceWithoutVat": {
                        return hiddenUserData(formatValue(props.value, column.clrType), { type: "price" });
                    }
                    case "output":
                    case "outputWithoutVat": {
                        return hiddenUserData(formatValue(props.value, column.clrType), { type: "pricePerVal" });
                    }
                    case "responsibleDisplayName": {
                        const onClick = () => {
                            props.modal({
                                name: "infoModal",
                                windowMod: "wide",
                                title: props.value,
                                body: props.renderRowSubComponent(props.row),
                            });
                        };
                        return (
                            <div className="cursor-pointer" onClick={onClick}>
                                <i
                                    className={cn("r-item__expander", {
                                        "icon-uEAAE-angle-right-solid": !props.row.isExpanded,
                                        "icon-uEA61-angle-down": props.row.isExpanded,
                                    })}
                                />
                                {props.value}
                            </div>
                        );
                    }
                    case "emptyMileage": {
                        const { emptyMileageRating } = props.row;
                        return `${formatValue(props.value, column.clrType)} (${formatValue(emptyMileageRating / 100, "percent")})`;
                    }
                    default:
                        return formatValue(props.value, column.clrType);
                }
            },
            Footer: () => {
                const defaultFooter = this.footerVal(column);
                const { footer } = this.props.data || {};
                switch (column.propertyName) {
                    case "EmptyMileage": {
                        const { emptyMileageRating } = footer;
                        return [
                            <span key="value">{defaultFooter}</span>,
                            <span key="rating"> ({formatValue(emptyMileageRating / 100, "percent")})</span>,
                        ];
                    }
                    case "overallBidsPrice":
                    case "overallBidsPriceWithoutVat": {
                        return hiddenUserData(defaultFooter, { type: "price" });
                    }
                    case "output":
                    case "outputWithoutVat": {
                        return hiddenUserData(defaultFooter, { type: "pricePerVal" });
                    }
                    default:
                        return defaultFooter;
                }
            },
            ...typeSpecificProps(column),
        };
    };

    extractColumns = () => {
        const { columns } = this.props.data || {};
        return [this.idColumn].concat(
            columns.map((column) => {
                if (column.propertyName === "ResponsibleDisplayName") {
                    column.label = "Ответственный";
                }
                return this.cellProps(column);
            }),
        );
    };

    tableExtraProps = () => {
        const { columns } = this.props.data || {};
        const hiddenColumns = columns
            .filter(
                (column) =>
                    column.isAdditionalColumn ||
                    column.isFooterData ||
                    ["ResponsibleId", "Bids"].includes(column.propertyName),
            )
            .map(columnName);
        return {
            initialState: { hiddenColumns },
            renderRowSubComponent: (row, isPrintForm) => {
                if (!row.original.bids || row.original.bids.length === 0) {
                    return null;
                }
                const tableProps = isPrintForm
                    ? {
                          className: "p-3 reports--bg-bluelight",
                          style: {
                              minWidth: "100%",
                              width: row.cells.reduce((acc, cell) => acc + cell.column.width, 0),
                          },
                      }
                    : {
                          className: "table-responsive",
                      };
                return (
                    <div {...tableProps}>
                        <table className="table table-sm mb-0 border-bottom border-secondary bg-secondary">
                            <thead>
                                <tr className="table-active">
                                    <th>#</th>
                                    <th>Заявка</th>
                                    <th>Внешний ID Заявки</th>
                                    <th>Контрагент</th>
                                    <th>Маршрут</th>
                                    <th>Водитель</th>
                                    <th>Даты выполнения</th>
                                    <th>Активный, км</th>
                                    {/* <th>Порожний, км</th>
                                    <th>Общий, км</th> */}
                                    <th>ВАЛ</th>
                                    <th>{upperFirst(this.props.organization?.currencyData?.name)}/км</th>
                                </tr>
                            </thead>
                            <tbody>
                                {row.original.bids.map((item, index) => (
                                    <tr key={index} className={item.compoundBidId && "r-item__compound-bid"}>
                                        <td>{index + 1}</td>
                                        <td data-bidid={item.id}>
                                            <a
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={paths.bidShow("bid", item.id)}
                                            >
                                                {item.id}
                                            </a>
                                            {item.hasManualMileage && (
                                                <Tooltip placement="top" overlay="Закрыта вручную">
                                                    <span className="pl-1 icon-uEA83-user-edit b-point__tooltip-icon" />
                                                </Tooltip>
                                            )}
                                            {item.compoundBidId && (
                                                <Tooltip placement="top" overlay="Сборная заявка">
                                                    <i className="pl-1 b-table__compound-icon icon-uEA76-animation-outline" />
                                                </Tooltip>
                                            )}
                                            {item.hasCarChange && (
                                                <Tooltip overlay="Произошла смена машины">
                                                    <span className="pl-1 icon-uEAB3-arrows-rotate b-point__tooltip-icon" />
                                                </Tooltip>
                                            )}
                                            {item.hasEmulatedMileage && (
                                                <Tooltip overlay="В заявке присутствуют сэмулированные участки маршрута">
                                                    <i className="pl-1 icon-uEACB-emulated-route b-point__tooltip-icon" />
                                                </Tooltip>
                                            )}
                                        </td>
                                        <td data-externalid={item.id}>{item.externalId}</td>
                                        <td data={item.id}>{item.counterpartyName}</td>
                                        <td data-route={item.id}>{item.route}</td>
                                        <td data={item.id}>{item.driverName}</td>
                                        <td data-executiondaterange={item.id}>{item.executionDateRange}</td>
                                        <td data-activemileage={item.id}>
                                            {formatValue(item.activeMileage, "double")}
                                        </td>
                                        {/* <td>{formatValue(item.emptyMileage, "double")}</td>
                                        <td>{formatValue(item.overallMileage, "double")}</td> */}
                                        <td data-overallbidprice={item.id}>
                                            {hiddenUserData(formatValue(item.overallBidPrice, "double"), {
                                                type: "price",
                                            })}
                                        </td>
                                        <td data-output={item.id}>
                                            {hiddenUserData(formatValue(item.output, "double"), {
                                                type: "pricePerVal",
                                            })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            },
        };
    };
}
